import { addToast } from '@heroui/toast';

export const showSuccessToast = (title: string, description: string) => {
    addToast({
        title,
        description,
        color: 'success',
    });
};

export const showErrorToast = (title: string, description: string) => {
    addToast({
        title,
        description,
        color: 'danger',
    });
};

export const showInfoToast = (title: string, description: string) => {
    addToast({
        title,
        description,
        color: 'primary',
    });
};

// Common auth-related toasts
export const authToasts = {
    loginSuccess: () => showSuccessToast('Login Successful', 'Welcome back!'),
    loginFailed: (message: string) => showErrorToast('Login Failed', message),
    registrationSuccess: () =>
        showSuccessToast(
            'Account Created',
            '🎉 Your account has been created successfully!'
        ),
    registrationFailed: (message: string) =>
        showErrorToast('Registration Failed', message),
    otpSent: () =>
        showSuccessToast(
            'OTP Sent',
            'Check your email for the one-time password.'
        ),
    otpFailed: (message: string) => showErrorToast('Error', message),
    logoutSuccess: () =>
        showSuccessToast('Logout Successful', 'You have been logged out.'),
    logoutFailed: (message: string) => showErrorToast('Logout Failed', message),
    emailVerificationSent: () =>
        showSuccessToast(
            'Email Sent',
            'Check your inbox for the verification email.'
        ),
    emailVerificationFailed: (message: string) =>
        showErrorToast('Resend Failed', message),
    emailVerificationSuccess: () =>
        showSuccessToast(
            'Email Verified',
            'Your email address has been successfully verified.'
        ),
    // Two-factor authentication related toasts
    twoFactorVerificationFailed: (message: string) =>
        showErrorToast(
            'Verification Failed',
            message || 'Invalid verification code.'
        ),
    twoFactorVerificationSuccess: () =>
        showSuccessToast('Login Successful', 'Welcome back!'),
    // Password reset related toasts
    passwordResetCodeSent: () =>
        showSuccessToast(
            'Verification Code Sent',
            'Check your email for a 6-digit verification code. It will expire in 10 minutes.'
        ),
    passwordResetCodeResent: () =>
        showSuccessToast(
            'Verification Code Sent',
            'A new verification code has been sent to your email.'
        ),
    passwordResetSuccess: () =>
        showSuccessToast(
            'Password Reset',
            'Your password has been reset successfully. Redirecting to login...'
        ),
    passwordResetFailed: (message: string) =>
        showErrorToast(
            'Invalid Code',
            message || 'The verification code is incorrect or has expired.'
        ),
    passwordResetError: (message: string) =>
        showErrorToast('Error', message || 'An unexpected error occurred.'),
    // Form validation toasts
    invalidInput: (message: string) =>
        showErrorToast('Invalid Input', message || 'Please check your input.'),
    // Profile update toasts
    profileUpdateFailed: (message: string) =>
        showErrorToast(
            'Update Failed',
            message || 'Failed to update your profile.'
        ),
    // Backup codes toasts
    backupCodesRetrieved: () =>
        showSuccessToast('Success', 'Backup codes retrieved successfully'),
    backupCodesError: (message: string) =>
        showErrorToast('Error', message || 'Failed to retrieve backup codes'),
    backupCodesGenerated: () =>
        showSuccessToast('Success', 'New backup codes generated successfully'),
    backupCodesGenerationFailed: (message: string) =>
        showErrorToast('Error', message || 'Failed to regenerate backup codes'),
    twoFactorEnableFailed: (message: string) =>
        showErrorToast(
            'Error',
            message || 'Failed to enable two-factor authentication'
        ),
    twoFactorEnabled: () =>
        showSuccessToast(
            'Success',
            'Two-factor authentication has been enabled!'
        ),
    twoFactorDisableFailed: (message: string) =>
        showErrorToast(
            'Error',
            message || 'Failed to disable two-factor authentication'
        ),
    twoFactorDisabled: () =>
        showSuccessToast(
            'Success',
            'Two-factor authentication has been disabled!'
        ),
    emailUpdateFail: (message: string) =>
        showErrorToast('Error', message || 'Failed to update email address'),
    emailUpdateSuccess: () =>
        showSuccessToast(
            'Success',
            'Your email address has been updated successfully!'
        ),
    emailChangeVerificationSent: () =>
        showSuccessToast(
            'Verification Email Sent',
            'Check your new email address for a verification link to complete the change.'
        ),
    accountDeleteFailed: (message: string) =>
        showErrorToast(
            'Error',
            message || 'Failed to delete your account. Please try again later.'
        ),
    accountDeleteSuccess: () =>
        showSuccessToast(
            'Account Deleted',
            'Your account has been deleted successfully. We are sorry to see you go.'
        ),
    accountDeleteVerificationSent: () =>
        showSuccessToast(
            'Account Deletion Verification Sent',
            'A verification email has been sent to confirm your account deletion. Please check your inbox.'
        ),
    // Passkey toasts
    passkeyRegistrationFailed: (message: string) =>
        showErrorToast(
            'Passkey Registration Failed',
            message ||
                'The process either timed out or was cancelled. Please try again.'
        ),
    passkeyRegistrationSuccess: () =>
        showSuccessToast(
            'Passkey Registered',
            'Your new passkey has been added successfully.'
        ),
    passkeyDeleteFailed: (message: string) =>
        showErrorToast(
            'Passkey Deletion Failed',
            message || 'An unexpected error occurred.'
        ),
    passkeyDeleteSuccess: () =>
        showSuccessToast(
            'Passkey Deleted',
            'Your passkey has been successfully removed.'
        ),
    passkeyUpdateFailed: (message: string) =>
        showErrorToast(
            'Passkey Update Failed',
            message || 'An unexpected error occurred.'
        ),
    passkeyUpdateSuccess: () =>
        showSuccessToast(
            'Passkey Updated',
            'Your passkey has been successfully updated.'
        ),
};
